import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { AssetType, Asset } from '../types.ts';
import { AssetModal, DeleteConfirmationModal } from './AssetModal.tsx';
import { 
  Plus, 
  ArrowRight, 
  Image as ImageIcon, 
  Video, 
  MessageSquare, 
  Maximize2 
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { assets, campaigns, updateAsset, deleteAsset, userProfile } = useStore();
  const { user } = useAuth();
  const recentAssets = assets.slice(0, 4);

  // Modal State
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const activeAsset = assets.find(a => a.id === selectedAssetId);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    assetId: string | null;
  }>({ isOpen: false, assetId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Get first name for personalized greeting, fallback to 'Creator'
  const displayName = user?.displayName || 'Creator';
  const firstName = displayName.split(' ')[0];

  const getCreatorDisplay = (asset: Asset) => {
    const isOwner = user?.uid === asset.createdBy;
    const name = isOwner 
      ? (user?.displayName || 'You') 
      : (asset.createdByName && asset.createdByName !== 'Unknown Creator' ? asset.createdByName : 'Unknown Creator');
    const avatar = isOwner 
      ? (user?.photoURL || userProfile?.photoURL || asset.createdByAvatar) 
      : asset.createdByAvatar;
    return { name, avatar };
  };

  const handleAssetClick = (asset: Asset) => {
    setSelectedAssetId(asset.id);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteConfirm({ isOpen: true, assetId: id });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.assetId) return;
    setIsDeleting(true);
    try {
      await deleteAsset(deleteConfirm.assetId);
      setDeleteConfirm({ isOpen: false, assetId: null });
      setSelectedAssetId(null);
    } catch (e) {
      console.error(e);
      alert("Failed to delete asset.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[var(--text-primary)]">Hello, {firstName}! 👋</h1>
          <p className="text-[var(--text-secondary)] mt-1">Ready to create something happy with Chip today?</p>
        </div>
        <Link 
          to="/generate"
          className="btn-primary flex items-center gap-2 shadow-lg hover:shadow-xl"
        >
          <Plus size={20} />
          Create New Chip
        </Link>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/generate" className="card p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group bg-gradient-to-br from-[var(--bg-primary)] to-[var(--bg-secondary)]">
          <div className="w-14 h-14 bg-blue-500/10 text-blue-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <ImageIcon size={28} />
          </div>
          <h3 className="text-lg font-heading font-semibold text-[var(--text-primary)]">Generate Image</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Create social posts, ads, or presentation assets.</p>
        </Link>
        <Link to="/video" className="card p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group bg-gradient-to-br from-[var(--bg-primary)] to-[var(--bg-secondary)]">
          <div className="w-14 h-14 bg-purple-500/10 text-purple-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <Video size={28} />
          </div>
          <h3 className="text-lg font-heading font-semibold text-[var(--text-primary)]">Video Studio</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Animate Chip for reels, stories, or loops.</p>
        </Link>
        <Link to="/captions" className="card p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group bg-gradient-to-br from-[var(--bg-primary)] to-[var(--bg-secondary)]">
          <div className="w-14 h-14 bg-green-500/10 text-green-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <MessageSquare size={28} />
          </div>
          <h3 className="text-lg font-heading font-semibold text-[var(--text-primary)]">Caption Vault</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Generate on-brand copy and hashtags.</p>
        </Link>
      </div>

      {/* Recent Generations */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-[var(--text-primary)]">Recent Generations</h2>
          <Link to="/library" className="text-sm text-[var(--text-primary)] font-medium hover:text-[var(--accent-primary)] flex items-center gap-1 transition-colors">
            View All <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {recentAssets.map(asset => (
            <div 
              key={asset.id} 
              className="relative aspect-square card overflow-hidden group shadow-sm hover:shadow-md cursor-pointer border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-all"
              onClick={() => handleAssetClick(asset)}
            >
               {asset.type === AssetType.Caption ? (
                 <div className="p-6 h-full flex flex-col justify-center bg-[var(--bg-tertiary)]">
                   <MessageSquare className="text-[var(--text-tertiary)] mb-3" size={24} />
                   <p className="text-xs text-[var(--text-secondary)] line-clamp-4 font-mono leading-relaxed">{asset.url}</p>
                 </div>
               ) : asset.type === AssetType.Video ? (
                 <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white">
                    <Video size={48} className="mb-2 opacity-80" />
                    <span className="text-[10px] uppercase font-bold tracking-wider opacity-60">Video</span>
                 </div>
               ) : (
                 <img src={asset.url} alt="Asset" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
               )}
               
               {/* Hover Overlay */}
               <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <span className="px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-xs font-bold text-black shadow-lg flex items-center gap-1">
                    <Maximize2 size={12} /> View Details
                  </span>
               </div>
               
               {/* Label */}
               <div className="absolute bottom-3 left-3 right-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300 pointer-events-none">
                 <div className="p-2.5 rounded-lg text-xs font-medium bg-black/70 text-white backdrop-blur-md shadow-lg border border-white/10 truncate">
                    {asset.scenario}
                 </div>
               </div>
            </div>
          ))}
          {recentAssets.length === 0 && (
            <div className="col-span-full py-16 text-center text-[var(--text-tertiary)] card border-dashed border-2 border-[var(--border-subtle)] bg-transparent shadow-none">
              No generations yet. Start creating!
            </div>
          )}
        </div>
      </div>

      {/* Active Campaigns */}
      <div className="card p-8 shadow-sm">
        <h2 className="text-xl font-heading font-bold text-[var(--text-primary)] mb-6">Active Campaigns</h2>
        <div className="space-y-4">
          {campaigns.filter(c => c.status === 'active').map(campaign => {
            const realAssetCount = assets.filter(a => a.campaignId === campaign.id).length;
            
            return (
              <div key={campaign.id} className="flex items-center justify-between p-5 bg-[var(--bg-secondary)] rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--border-default)] transition-colors group">
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)] text-lg">{campaign.name}</h4>
                  <p className="text-sm text-[var(--text-secondary)] mt-1">
                    {campaign.description || "No description provided"}
                  </p>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <span className="block text-2xl font-bold text-[var(--text-primary)]">{realAssetCount}</span>
                    <span className="text-[10px] text-[var(--text-tertiary)] uppercase font-bold tracking-wider">Assets</span>
                  </div>
                  <div className="h-10 w-px bg-[var(--border-default)]" />
                  <Link to={`/library?campaign=${campaign.id}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--bg-primary)] text-[var(--text-tertiary)] border border-[var(--border-subtle)] group-hover:text-[var(--text-primary)] group-hover:border-[var(--text-primary)] group-hover:scale-110 transition-all shadow-sm">
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            );
          })}
          {campaigns.filter(c => c.status === 'active').length === 0 && (
             <div className="text-center py-8 text-[var(--text-tertiary)] italic">
               No active campaigns. Create one in the Asset Library or Campaigns tab.
             </div>
          )}
        </div>
      </div>

      {/* Asset Modal for Recent Generations */}
      {activeAsset && (
        <AssetModal 
          asset={activeAsset} 
          onClose={() => setSelectedAssetId(null)}
          onUpdate={updateAsset}
          onRequestDelete={handleDeleteRequest}
          campaigns={campaigns}
          creatorInfo={getCreatorDisplay(activeAsset)}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmationModal 
        isOpen={deleteConfirm.isOpen}
        title="Delete Asset?"
        message="Are you sure you want to delete this asset? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, assetId: null })}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default Dashboard;